import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  CardMedia,
  Skeleton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { toast } from "../../ui";
import moment from "moment";
import axios from "axios";

// Web3 & Services
import cinstance from "../../../Service/randomcinstance";
import usercinstance from "../../../Service/usercinstance";
import giftcardcinstance from "../../../Service/giftcardcinstance";
import web3 from "../../../Service/web3";
import getImageUrlByHash from "../../../../static/logoUtils";
import AutobetLogo from "../../../../static/Images/logo.png";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import { Path, getNetworkCurrency } from "../../../../static/Constants";

// Dashboard Layout & Components
import { DashboardLayout } from "../layout";

// Centralized UI Components
import {
  CommonButton,
  CommonChip,
  CommonPageHeader,
  CommonTable,
} from "../../ui";

const NoBuyer = "0x0000000000000000000000000000000000000000";

const withTimeout = (promise, ms = 4000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), ms)
    ),
  ]);
};

const safeFromWei = (val, unit = "ether") => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, unit);
  } catch (err) {
    try {
      const num = Number(val);
      if (isNaN(num)) return "0";
      return (num / 1e18).toFixed(4);
    } catch {
      return "0";
    }
  }
};

const GiftCardDetailsPage = ({ role }) => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Determine role: explicitly passed or derived from URL
  const currentRole =
    role || (location.pathname.startsWith("/admin") ? "admin" : "creator");
  const isAdmin = currentRole === "admin";

  // Authentication & Web3 State
  const [account, setAccount] = useState("");
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(true);

  // Data State
  const [giftcard, setGiftCard] = useState(null);
  const [giftcardLogoData, setGiftcardLogoData] = useState([]);
  const [copiedKey, setCopiedKey] = useState(null);
  const [networkId, setNetworkId] = useState(null);

  useEffect(() => {
    const fetchNetId = async () => {
      try {
        if (web3 && web3.eth) {
          const id = await web3.eth.net.getId();
          setNetworkId(id);
        }
      } catch (e) {
        console.error("Error fetching network ID:", e);
      }
    };
    fetchNetId();
  }, []);

  const currency = useMemo(() => getNetworkCurrency(networkId), [networkId]);

  const apiUrl = process.env.REACT_APP_API_KEY;
  const currentDT = moment().unix();

  /**
   * Fetch Logos from Backend API
   */
  useEffect(() => {
    if (!apiUrl) return;
    axios
      .get(`${apiUrl}/giftcardlogos/getAllGiftcardLogos`)
      .then((response) => {
        if (response?.data?.result) {
          setGiftcardLogoData(response.data.result);
        }
      })
      .catch((err) => {
        console.warn("Could not fetch giftcard logos:", err);
      });
  }, [apiUrl]);

  /**
   * Fetch Single Gift Card Details from Contract
   */
  const fetchCardDetails = useCallback(async () => {
    if (!id) return;

    try {
      if (!window.ethereum && !web3) {
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      window.web3 = web3;
      let accounts = [];
      try {
        if (window.ethereum?.request) {
          accounts = await window.ethereum.request({ method: "eth_accounts" });
        }
        if (!accounts || accounts.length === 0) {
          accounts = await web3.eth.getAccounts();
        }
      } catch (accErr) {
        console.warn("Account retrieval warning:", accErr);
      }

      const userAccount = accounts && accounts.length > 0 ? accounts[0] : "";
      setAccount(userAccount);

      if (isAdmin) {
        // Admin Access Verification
        let contractAdmin = "";
        try {
          if (cinstance?.methods?.admin) {
            contractAdmin = await withTimeout(
              cinstance.methods.admin().call(),
              3000
            );
          }
        } catch (adminErr) {
          console.warn("Could not fetch contract admin:", adminErr);
        }

        const authorized = Boolean(
          contractAdmin &&
            userAccount &&
            contractAdmin.toLowerCase() === userAccount.toLowerCase()
        );
        setIsAuthorized(authorized);
        setAuthChecked(true);

        if (!authorized) {
          setLoading(false);
          return;
        }
      } else {
        // Creator Access Verification
        try {
          if (usercinstance?.methods?.organisationbyaddr && userAccount) {
            const orgData = await usercinstance.methods
              .organisationbyaddr(userAccount)
              .call();
            setCreatorInfo(orgData || { name: "Creator", active: true });
            setIsAuthorized(Boolean(orgData ? orgData.active : true));
          } else {
            setCreatorInfo({ name: "Creator", active: true });
            setIsAuthorized(true);
          }
        } catch (orgErr) {
          console.warn("Could not fetch creator org info", orgErr);
          setCreatorInfo({ name: "Creator", active: true });
          setIsAuthorized(true);
        }
        setAuthChecked(true);
      }

      // Fetch Gift Card Data
      if (giftcardcinstance?.methods?.giftCards) {
        const res = await withTimeout(
          giftcardcinstance.methods.giftCards(id).call(),
          4000
        );

        if (
          res &&
          res.creator &&
          res.creator !== "0x0000000000000000000000000000000000000000"
        ) {
          setGiftCard({ ...res, giftCardId: id });
        } else {
          setGiftCard(null);
        }
      }
    } catch (err) {
      console.error("Error fetching gift card details:", err);
      toast.error("Failed to load gift card details from blockchain.");
    } finally {
      setLoading(false);
    }
  }, [id, isAdmin]);

  useEffect(() => {
    fetchCardDetails();

    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          fetchCardDetails();
        } else {
          setAccount("");
          setIsAuthorized(false);
        }
      };
      window.ethereum.on("accountsChanged", handleAccountsChanged);
      return () => {
        window.ethereum.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );
      };
    }
  }, [fetchCardDetails]);

  /**
   * Clipboard Helper
   */
  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
    toast.success("Copied to clipboard!");
  };

  const imageUrl =
    (giftcard && getImageUrlByHash(giftcard.logoHash, giftcardLogoData)) ||
    AutobetLogo;

  // Bought By Table Data & Columns
  const buyerRows = useMemo(() => {
    if (giftcard && giftcard.buyer && giftcard.buyer !== NoBuyer) {
      return [{ id: giftcard.giftCardId || 1, buyer: giftcard.buyer }];
    }
    return [];
  }, [giftcard]);

  const buyerColumns = useMemo(
    () => [
      {
        id: "buyer",
        label: "Bought By",
        minWidth: 280,
        render: (_, row) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontWeight: 600,
                color: colors.slate[900],
              }}
            >
              {row?.buyer}
            </Typography>
            <Tooltip
              title={copiedKey === "buyer-addr" ? "Copied!" : "Copy Address"}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(row?.buyer, "buyer-addr");
                }}
                sx={{ p: 0.5, color: colors.slate[500] }}
              >
                {copiedKey === "buyer-addr" ? (
                  <CheckIcon
                    sx={{ fontSize: 15, color: colors.success.main }}
                  />
                ) : (
                  <CopyIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [copiedKey]
  );

  const backUrl = isAdmin ? Path.admingiftcard : Path.mygiftcard;
  const dashboardUrl = isAdmin ? Path.admin : Path.org;
  const listTitle = isAdmin ? "Gift Cards List" : "My Gift Cards";

  return (
    <DashboardLayout
      role={currentRole}
      title={`Gift Card #${id}`}
      subtitle="View on-chain gift card specifications and redemption details."
      breadcrumbs={[
        { label: "Dashboard", href: dashboardUrl },
        { label: listTitle, href: backUrl },
        { label: `Gift Card #${id}` },
      ]}
      headerActions={
        <CommonButton
          variant="outline"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push(backUrl)}
        >
          Back to List
        </CommonButton>
      }
      creatorAddress={account}
      userName={
        isAdmin
          ? "AutoBET Admin"
          : creatorInfo?.name || "Creator"
      }
      userRole={isAdmin ? "Administrator" : "Creator"}
      authChecked={authChecked}
      isAuthorized={isAuthorized}
      accessDeniedProps={{
        title: isAdmin ? "Admin Access Required" : "Account Access Denied",
        message: isAdmin
          ? "You must connect with the verified AutoBET smart contract administrator wallet to view Gift Card details."
          : "You must be a registered Creator or organization to view Gift Card details.",
        actionText: `Back to ${listTitle}`,
        onAction: () => history.push(backUrl),
      }}
    >
      {/* Loading Skeleton */}
          {loading && (
            <Box>
              <Box
                sx={{
                  p: { xs: 2.5, sm: 3.5 },
                  bgcolor: colors.background.paper,
                  borderRadius: borderRadiusPx.xl,
                  boxShadow: customShadows.card,
                  border: `1px solid ${colors.slate[200]}`,
                  mb: 3.5,
                }}
              >
                <Grid container spacing={4}>
                  {/* Left Column: Card Image Skeleton */}
                  <Grid item xs={12} md={6}>
                    <Skeleton
                      variant="text"
                      width={100}
                      height={20}
                      animation="wave"
                      sx={{ mb: 1.5 }}
                    />
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height={180}
                      animation="wave"
                      sx={{ maxWidth: 320, borderRadius: borderRadiusPx.lg }}
                    />
                  </Grid>

                  {/* Right Column: Gift Card Fields Skeleton */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2.5}>
                      {/* Created By */}
                      <Box>
                        <Skeleton
                          variant="text"
                          width={80}
                          height={14}
                          animation="wave"
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton
                          variant="text"
                          width="85%"
                          height={20}
                          animation="wave"
                        />
                      </Box>
                      {/* Amount */}
                      <Box>
                        <Skeleton
                          variant="text"
                          width={60}
                          height={14}
                          animation="wave"
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton
                          variant="text"
                          width={140}
                          height={26}
                          animation="wave"
                        />
                      </Box>
                      {/* Price */}
                      <Box>
                        <Skeleton
                          variant="text"
                          width={50}
                          height={14}
                          animation="wave"
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton
                          variant="text"
                          width={120}
                          height={26}
                          animation="wave"
                        />
                      </Box>
                      {/* Status */}
                      <Box>
                        <Skeleton
                          variant="text"
                          width={50}
                          height={14}
                          animation="wave"
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton
                          variant="rounded"
                          width={80}
                          height={24}
                          animation="wave"
                          sx={{ borderRadius: borderRadiusPx.full }}
                        />
                      </Box>
                      {/* Expiry Date */}
                      <Box>
                        <Skeleton
                          variant="text"
                          width={80}
                          height={14}
                          animation="wave"
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton
                          variant="text"
                          width={160}
                          height={20}
                          animation="wave"
                        />
                      </Box>
                    </Stack>
                  </Grid>

                  {/* Description Skeleton */}
                  <Grid item xs={12}>
                    <Box
                      sx={{ pt: 2, borderTop: `1px solid ${colors.slate[100]}` }}
                    >
                      <Skeleton
                        variant="text"
                        width={90}
                        height={16}
                        animation="wave"
                        sx={{ mb: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width="95%"
                        height={18}
                        animation="wave"
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={18}
                        animation="wave"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Bought By Table Skeleton */}
              <Box
                sx={{
                  bgcolor: colors.background.paper,
                  borderRadius: borderRadiusPx.xl,
                  boxShadow: customShadows.card,
                  border: `1px solid ${colors.slate[200]}`,
                  overflow: "hidden",
                }}
              >
                <CommonTable
                  columns={buyerColumns}
                  rows={[]}
                  loading={true}
                  rowKey="id"
                  emptyMessage="No Data !"
                />
              </Box>
            </Box>
          )}

          {/* Not Found State */}
          {!loading && !giftcard && (
            <Box
              sx={{
                p: 6,
                textAlign: "center",
                bgcolor: colors.background.paper,
                borderRadius: borderRadiusPx.xl,
                boxShadow: customShadows.card,
                border: `1px solid ${colors.slate[200]}`,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: colors.text.primary,
                  mb: 1,
                }}
              >
                No Data !
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, mb: 2.5 }}
              >
                Could not find gift card #{id} on the blockchain.
              </Typography>
              <CommonButton
                variant="primary"
                onClick={() => history.push(backUrl)}
              >
                Return to {listTitle}
              </CommonButton>
            </Box>
          )}

          {/* Main Details Card (Strictly containing the exact fields from old code) */}
          {!loading && giftcard && (
            <Box>
              <Box
                sx={{
                  p: { xs: 2.5, sm: 3.5 },
                  bgcolor: colors.background.paper,
                  borderRadius: borderRadiusPx.xl,
                  boxShadow: customShadows.card,
                  border: `1px solid ${colors.slate[200]}`,
                  mb: 3.5,
                }}
              >
                <Grid container spacing={4}>
                  {/* Left Column: Card Image */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: colors.text.primary,
                        mb: 1.5,
                      }}
                    >
                      Card Image :
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 320,
                        height: 180,
                        borderRadius: borderRadiusPx.lg,
                        overflow: "hidden",
                        border: `1px solid ${colors.slate[200]}`,
                        bgcolor: colors.slate[900],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={
                          giftcard.logoHash !== undefined &&
                          giftcard.logoHash !== ""
                            ? imageUrl
                            : AutobetLogo
                        }
                        alt={giftcard.tokenName || "Gift Card"}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Right Column: Gift Card Fields */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      {/* Created By */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 600,
                            display: "block",
                          }}
                        >
                          Created By :
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mt: 0.25 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              color: colors.text.primary,
                              wordBreak: "break-all",
                            }}
                          >
                            {giftcard.creator}
                          </Typography>
                          <Tooltip
                            title={
                              copiedKey === "creator-addr"
                                ? "Copied!"
                                : "Copy Address"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopy(giftcard.creator, "creator-addr")
                              }
                              sx={{ p: 0.5, color: colors.slate[500] }}
                            >
                              {copiedKey === "creator-addr" ? (
                                <CheckIcon
                                  sx={{
                                    fontSize: 15,
                                    color: colors.success.main,
                                  }}
                                />
                              ) : (
                                <CopyIcon sx={{ fontSize: 15 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>

                      {/* Amount */}
                      {giftcard.BETValue && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 600,
                              display: "block",
                            }}
                          >
                            Amount :
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 700,
                              color: colors.primary.main,
                              mt: 0.25,
                            }}
                          >
                            {safeFromWei(giftcard.BETValue)}{" "}
                            {giftcard.tokenName}
                          </Typography>
                        </Box>
                      )}

                      {/* Price */}
                      {giftcard.ETHPrice && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 600,
                              display: "block",
                            }}
                          >
                            Price :
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 700,
                              color: colors.amber[700],
                              mt: 0.25,
                            }}
                          >
                            {safeFromWei(giftcard.ETHPrice)} {currency}
                          </Typography>
                        </Box>
                      )}

                      {/* Status */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 600,
                            display: "block",
                          }}
                        >
                          Status :
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {giftcard.isSold === false &&
                            Number(giftcard.expDate) > currentDT && (
                              <CommonChip
                                label="Active"
                                variant="success"
                                size="small"
                              />
                            )}
                          {giftcard.isSold === true && (
                            <CommonChip
                              label="Redeemed"
                              variant="neutral"
                              size="small"
                            />
                          )}
                          {giftcard.isSold === false &&
                            Number(giftcard.expDate) < currentDT && (
                              <CommonChip
                                label="Expired"
                                variant="danger"
                                size="small"
                              />
                            )}
                        </Box>
                      </Box>

                      {/* Expiry Date */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 600,
                            display: "block",
                          }}
                        >
                          Expiry Date :
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: colors.text.primary,
                            mt: 0.25,
                          }}
                        >
                          {moment
                            .unix(giftcard.expDate)
                            .format("DD/MM/YYYY hh:mma")}
                        </Typography>
                      </Box>

                      {/* Voucher Code (conditional if present) */}
                      {giftcard.voucherCode !== "" &&
                        giftcard.voucherCode !== undefined && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text.secondary,
                                fontWeight: 600,
                                display: "block",
                              }}
                            >
                              Voucher Code :
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: colors.text.primary,
                                mt: 0.25,
                              }}
                            >
                              {giftcard.voucherCode}
                            </Typography>
                          </Box>
                        )}

                      {/* Token Transfered (conditional if expired & unsold) */}
                      {giftcard.isSold === false &&
                        Number(giftcard.expDate) < currentDT && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text.secondary,
                                fontWeight: 600,
                                display: "block",
                              }}
                            >
                              Token Transfered :
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: colors.text.primary,
                                mt: 0.25,
                              }}
                            >
                              {giftcard.status === "0"
                                ? "No"
                                : giftcard.status === "1"
                                ? "NA"
                                : "Yes"}
                            </Typography>
                          </Box>
                        )}
                    </Stack>
                  </Grid>

                  {/* Full Width Row: Description */}
                  <Grid item xs={12}>
                    <Box
                      sx={{ pt: 2, borderTop: `1px solid ${colors.slate[100]}` }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 600,
                          display: "block",
                        }}
                      >
                        Description :
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.primary,
                          mt: 0.5,
                          lineHeight: 1.6,
                        }}
                      >
                        {giftcard.Desc || "No description provided."}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Table: Bought By */}
              <Box
                sx={{
                  bgcolor: colors.background.paper,
                  borderRadius: borderRadiusPx.xl,
                  boxShadow: customShadows.card,
                  border: `1px solid ${colors.slate[200]}`,
                  overflow: "hidden",
                }}
              >
                <CommonTable
                  columns={buyerColumns}
                  rows={buyerRows}
                  rowKey="id"
                  emptyMessage="No Data !"
                />
              </Box>
            </Box>
          )}
    </DashboardLayout>
  );
};

GiftCardDetailsPage.propTypes = {
  role: PropTypes.oneOf(["admin", "creator"]),
};

export default GiftCardDetailsPage;
